import React from "react";
import {
  Box,
  Typography,
  Stack,
  Alert,
} from "@mui/material";
import {
  Warning as WarningIcon,
} from "@mui/icons-material";
import { ModalV2 } from "@/components";
import { useDeleteProductMutation } from "@/services/product-catalogue/pc-api";
import { ProductWithTariffs } from "@/services/product-catalogue/pc-api";

interface DeleteProductDialogProps {
  open: boolean;
  onClose: () => void;
  onProductDeleted: () => void;
  product: ProductWithTariffs | null;
}

const DeleteProductDialog: React.FC<DeleteProductDialogProps> = ({
  open,
  onClose,
  onProductDeleted,
  product,
}) => {
  const [deleteProduct, { isLoading }] = useDeleteProductMutation();

  const handleDelete = async () => {
    if (!product) return;
    
    try {
      await deleteProduct(product._id).unwrap();
      onProductDeleted();
      onClose();
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  if (!product) return null;

  return (
    <ModalV2
      show={open}
      setShow={onClose}
      title="Delete Product"
      subTitle="This action cannot be undone"
      buttonText="Delete Product"
      buttonAction={handleDelete}
      isConfirmButtonLoading={isLoading}
      buttonLoader={false}
      maxWidth="sm"
      modalStyles={{
        "& .MuiPaper-root": {
          maxWidth: "600px",
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Alert severity="error" sx={{ borderRadius: '12px' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this product? This will permanently remove:
            </Typography>
            
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
            }}>
              <Stack spacing={1}>
                <Typography variant="body2" color="#6b7280">
                  <Typography component="span" variant="body2" fontWeight={600}>SKU:</Typography> {product.sku}
                </Typography>
                <Typography variant="body2" color="#6b7280">
                  <Typography component="span" variant="body2" fontWeight={600}>Name:</Typography> {product.name}
                </Typography>
                <Typography variant="body2" color="#6b7280">
                  <Typography component="span" variant="body2" fontWeight={600}>HTS Code:</Typography> {product.htsCode}
                </Typography>
                <Typography variant="body2" color="#6b7280">
                  <Typography component="span" variant="body2" fontWeight={600}>Country of Origin:</Typography> {product.countryOfOrigin}
                </Typography>
              </Stack>
            </Box>
          </Alert>

          <Alert severity="warning" sx={{ borderRadius: '12px' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <WarningIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
              <Typography variant="body2" fontWeight={500}>
                This action is permanent and cannot be undone. All associated tariff calculations will be lost.
              </Typography>
            </Stack>
          </Alert>
        </Stack>
      </Box>
    </ModalV2>
  );
};

export default DeleteProductDialog; 